import { MedicalHistorySubmit } from '@brands/Dashboard/MedicalHistory/MedicalHistory/utils/types';

/**
 * The medical_history API rejects `null` for these fields (it wants a real
 * boolean or nothing) — but a not-yet-answered question can reach this point
 * as `null` either from a step's own initial state or from a fetched record,
 * since the API itself can return these fields as `null` for a patient who
 * has never fully completed medical history. Call this immediately before
 * every addClinicalData/updateClinicalData('medical_history', ...) so a step
 * that didn't set every field itself (either because the patient skipped it,
 * or because it isn't that step's own question) never forwards a stray null.
 *
 * In the regular intake these questions are required, so a missing value is
 * still normalized to `false` for the API. Clinical Enablement can make them
 * optional after an upload; in that flow `omitUnanswered` removes nullish
 * values instead. This is important because `false` is a real clinical answer
 * ("No"), not a harmless default.
 *
 * `interested_in_stopping` is deliberately excluded — unlike the other three,
 * it's a genuine tri-state field: `null` means "not applicable, no substance
 * use was reported," and the recap screen's own schema requires it stay
 * exactly `null` in that case.
 */
export const sanitizeMedicalHistoryBooleans = <T extends Partial<MedicalHistorySubmit>>(
  payload: T,
  omitUnanswered = false,
): T => {
  const sanitized: Partial<MedicalHistorySubmit> = { ...payload };

  if (omitUnanswered) {
    if (sanitized.under_care_of_physician == null) delete sanitized.under_care_of_physician;
    if (sanitized.taking_medication == null) delete sanitized.taking_medication;
    if (sanitized.allergic_to_medications == null) delete sanitized.allergic_to_medications;
  } else {
    sanitized.under_care_of_physician = sanitized.under_care_of_physician ?? false;
    sanitized.taking_medication = sanitized.taking_medication ?? false;
    sanitized.allergic_to_medications = sanitized.allergic_to_medications ?? false;
  }

  return sanitized as T;
};
